type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface RequestConfig {
  responseType?: 'blob',
  params?: Record<string, string | undefined>
}

interface BaseApi {
  get<T = any>(url: string, config?: RequestConfig): Promise<T>;
  post<T = any, U = any>(url: string, data: U, config?: RequestConfig): Promise<T>;
  put<T = any, U = any>(url: string, data: U, config?: RequestConfig): Promise<T>;
  delete<T = any>(url: string): Promise<T>;
}

export class ApiError extends Error {
  public status?: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

const isFormData = (data: any): boolean => {
  return typeof data === 'object' && data?.constructor?.name === 'FormData';
};

const apiUrl = process.env.MOCK_API_URL ?? '/api';
const getUrl = (url: string) => url.startsWith('http') ? url : `${apiUrl}${url}`;

const apiRequest = async (
  url: string,
  method: HttpMethod,
  data?: unknown,
  config?: RequestConfig & { headers?: Record<string, string> }
): Promise<any> => {
  const options: RequestInit = {
    method,
    // credentials: 'include',
    headers: config?.headers ?? {}
  };

  if (!isFormData(data)) {
    options.headers = {
      ...options.headers,
      'Content-Type': 'application/json'
    };
  }

  if (config?.params) {
    url += Object.entries(config.params).reduce((acc, [key, value]) => {
      if (!value) return acc;
      if (!acc) return `?${key}=${value}`;
      return `${acc}&${key}=${value}`;
    }, '');
  }

  if (data) {
    if (isFormData(data)) {
      options.body = data as any;
    } else {
      options.body = JSON.stringify(data);
    }
  }

  try {
    const response = await fetch(getUrl(url), options);

    if (!response.ok) {
      const result = await response.json();
      throw new ApiError(result.error, response.status);
    } else {
      if (config?.responseType === 'blob') {
        return await response.blob();
      } else if (config?.responseType === 'text') {
        return await response.text();
      } else {
        return await response.json();
      }
    }
  } catch (error) {
    console.error(`Error during ${method.toUpperCase()} request to ${url}:`, error);
    throw error;
  }
};

const baseApi: BaseApi = {
  get(url, config) {
    return apiRequest(url, 'GET', null, config);
  },

  post(url, data, config) {
    return apiRequest(url, 'POST', data, config);
  },

  put(url, data, config) {
    return apiRequest(url, 'PUT', data, config);
  },

  delete(url) {
    return apiRequest(url, 'DELETE');
  },
};

export default baseApi;
